import React, { useEffect, useState } from 'react';
import { Button, Checkbox, FormControlLabel, Typography, Box, CardHeader, Grid, Divider } from '@mui/material';
import FormModal from '../../common/formModal';
import Styles from '../../theme/commonCss/Styles';
import SearchField from '../../common/searchField';
import { TreeItem, TreeView } from '@mui/x-tree-view';
import { ReactComponent as ArrowDownOutlinedIcon } from '../../assets/icons/main/chevron-down-icon.svg';
import { ReactComponent as ArrowUpOutlinedIcon } from '../../assets/icons/main/chevron-right-icon.svg';

type User = {
    user_id: string;
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    // user_name?: string; // Remove if not needed
};

type Role = {
    role_name: string;
    role_id: string;
    users: User[];
};

type UseCaseAccessPopupProps = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (selectedAccess: { roles: string[]; users: string[] }, selectedUseCase: any) => void;
    rolesData: Role[]; // Data for roles and users
    initialSelectedRoles?: string[]; // Pre-selected roles
    initialSelectedUsers?: string[]; // Pre-selected users
    selectedUseCase?: any;
};

const UseCaseAccessPopup: React.FC<UseCaseAccessPopupProps> = ({
    isOpen,
    onClose,
    onSubmit,
    rolesData,
    initialSelectedRoles = [],
    initialSelectedUsers = [],
    selectedUseCase
}) => {
    const [selectedRoles, setSelectedRoles] = useState<string[]>(initialSelectedRoles);
    const [selectedUsers, setSelectedUsers] = useState<string[]>(initialSelectedUsers);
    const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
    const [mappedRoles, setMappedRoles] = useState<Role[]>([]);

    // useEffect(() => {
    //     if (isOpen) {
    //         setSelectedRoles(initialSelectedRoles);
    //         setSelectedUsers(initialSelectedUsers);
    //     }
    // }, [isOpen, initialSelectedRoles, initialSelectedUsers]);

    useEffect(() => {
    if (isOpen) {
        setSelectedRoles(initialSelectedRoles);

        // Add all users of selected roles to selectedUsers, plus any initialSelectedUsers
        const usersFromRoles = (rolesData || [])
            .filter((role: any) => initialSelectedRoles.includes(role.id))
            .flatMap((role: any) => (role.assigned_users || []).map((user: any) => user.id));

        // Combine and deduplicate
        const allSelectedUsers = Array.from(new Set([
            ...usersFromRoles,
            ...initialSelectedUsers
        ]));

        setSelectedUsers(allSelectedUsers);
    }
}, [isOpen, initialSelectedRoles, initialSelectedUsers, rolesData]);

    useEffect(() => {
        const _mappedRoles: Role[] = (rolesData || [])
            .map((role: any) => ({
                role_name: role.name,
                role_id: role.id,
                users: (role.assigned_users || []).map((user: any) => ({
                    user_id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    username: user.username,
                    email: user.email,
                })),
            }))
            .filter((role: Role) => role.users.length > 0); // filter roles with users only
        setMappedRoles(_mappedRoles);
    }, [rolesData]);

    // Handle role checkbox toggle
    const handleRoleToggle = (role: Role, isChecked: boolean) => {
        if (isChecked) {
            // Add role and all its users
            setSelectedRoles([...selectedRoles, role.role_id]);
            setSelectedUsers(Array.from(new Set([
                ...selectedUsers,
                ...role.users.map((user) => user.user_id)
            ])));
        } else {
            // Remove role and all its users
            setSelectedRoles(selectedRoles.filter((id) => id !== role.role_id));
            setSelectedUsers(selectedUsers.filter(
                (id) => !role.users.some((user) => user.user_id === id)
            ));
        }
    };

    // Handle user checkbox toggle
    const handleUserToggle = (user: User, role: Role, isChecked: boolean) => {
        let updatedUsers: string[];
        if (isChecked) {
            updatedUsers = Array.from(new Set([...selectedUsers, user.user_id]));
        } else {
            updatedUsers = selectedUsers.filter((id) => id !== user.user_id);
        }

        // Check if all users in this role are now selected
        const allRoleUsersSelected = role.users.every((u) => updatedUsers.includes(u.user_id));
        let updatedRoles = selectedRoles;
        if (allRoleUsersSelected) {
            // Add role to selectedRoles if not present
            if (!selectedRoles.includes(role.role_id)) {
                updatedRoles = [...selectedRoles, role.role_id];
            }
        } else {
            // Remove role from selectedRoles if present
            if (selectedRoles.includes(role.role_id)) {
                updatedRoles = selectedRoles.filter((id) => id !== role.role_id);
            }
        }

        setSelectedUsers(updatedUsers);
        setSelectedRoles(updatedRoles);
    };

    // Handle form submission
    const handleSubmit = (e: any) => {
        e.preventDefault();

        // 1. Get all user IDs that belong to selected roles
        const usersInSelectedRoles = mappedRoles
            .filter(role => selectedRoles.includes(role.role_id))
            .flatMap(role => role.users.map(user => user.user_id));

        // 2. Only include users that are selected AND NOT part of a selected role
        const individualUsers = selectedUsers.filter(
            userId => !usersInSelectedRoles.includes(userId)
        );

        // 3. Prepare payload
        // console.log('Selected Roles:', selectedRoles,individualUsers);
        onSubmit(
            { roles: selectedRoles, users: individualUsers },
            selectedUseCase
        );
        handleClose();
    };

    const handleClose = () => {
        setExpandedNodes([]);
        setSelectedRoles([]);      // Reset selected roles
        setSelectedUsers([]);
        onClose();
    }

    // Handle search functionality
    const handleSearch = (searchTerm: string) => {
        const lowerCaseSearchTerm = searchTerm.trim().toLowerCase();

        const matchingRoles = mappedRoles.filter((role) => {
            // Check role name
            if (role.role_name && role.role_name.toLowerCase().includes(lowerCaseSearchTerm)) {
                return true;
            }
            // Check users in the role
            return (role.users || []).some((user: any) => {
                const firstLast = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
                const lastFirst = `${user.lastName || ''} ${user.firstName || ''}`.toLowerCase();
                return (
                    firstLast.includes(lowerCaseSearchTerm) ||
                    lastFirst.includes(lowerCaseSearchTerm) ||
                    (user.email && user.email.toLowerCase().includes(lowerCaseSearchTerm)) ||
                    (user.username && user.username.toLowerCase().includes(lowerCaseSearchTerm))
                );
            });
        });

        const matchingRoleIds = matchingRoles.map((role) => `role-${role.role_id}`);
        setExpandedNodes(matchingRoleIds);
    };

    // Render tree nodes recursively
    const renderTree = (role: Role) => {
        const isRoleChecked = selectedRoles.includes(role.role_id);
        const isRoleIndeterminate =
            role.users.some((user) => selectedUsers.includes(user.user_id)) && !isRoleChecked;

        return (
            <TreeItem
                key={role.role_id}
                nodeId={`role-${role.role_id}`}
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            checked={isRoleChecked}
                            indeterminate={isRoleIndeterminate}
                            onChange={(e) => handleRoleToggle(role, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <Typography sx={{ fontWeight: '400', fontSize: '16px', color: '#434D67' }}>
                            {role.role_name}
                        </Typography>
                    </Box>
                }
            >
                {role.users.map((user) => (
                    <TreeItem
                        key={user.user_id}
                        nodeId={`user-${user.user_id}`}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                    checked={
                                        selectedRoles.includes(role.role_id) ||
                                        selectedUsers.includes(user.user_id)
                                    }
                                    onChange={(e) => handleUserToggle(user, role, e.target.checked)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <Typography sx={{ fontWeight: '400', fontSize: '16px', color: '#434D67' }}>
                                    {user?.firstName} {user?.lastName}
                                </Typography>
                            </Box>
                        }
                    />
                ))}
            </TreeItem>
        );
    };

    return (
        <FormModal title="GRAFANA DASHBOARD ACCESS" isOpen={isOpen} handleSubmit={handleSubmit} handleClose={handleClose}>
            <Grid container >
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <CardHeader title="Select Roles / Users" titleTypographyProps={Styles.title} sx={{ padding: 0 }} />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <SearchField searchPlaceholder="Search Roles / Users" onSearch={handleSearch} />
                </Grid>
            </Grid>
            <Divider sx={{ marginTop: '24px' }} />
            <Grid container sx={{ marginTop: '20px', height: '400px', overflowY: 'scroll' }}>
                <TreeView
                    aria-label="roles tree"
                    defaultCollapseIcon={<ArrowDownOutlinedIcon />}
                    defaultExpandIcon={<ArrowUpOutlinedIcon />}
                    expanded={expandedNodes}
                    onNodeToggle={(event, nodeIds) => setExpandedNodes(nodeIds)}
                    sx={{ overflowY: 'auto', maxHeight: '400px', width: '100%' }}
                >
                    {mappedRoles.map((role) => renderTree(role))}
                </TreeView>
            </Grid>
        </FormModal>
    );
};

export default UseCaseAccessPopup;